
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  LinearProgress,
  Chip,
  Button,
  TextField,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  SearchRounded,
  QuestionAnswer,
  QuestionAnswerOutlined,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getCvForms,
  createCvForm,
  deleteCvForm,
  deleteCvQuestion,
  getCvJobs,
  getCvFormQuestions,
  getCvFormDetails,
  updateCvForm,
  reorderCvQuestions,
  createCvQuestion,
  updateCvQuestion,
} from '../../../../services/api';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { QuestionDialog } from './FormEditor';

const QUESTION_TYPE_LABELS = {
  text: 'Texto',
  number: 'Número',
  select: 'Múltipla escolha',
  multiselect: 'Múltipla escolha (várias)',
  boolean: 'Sim/Não',
  likert: 'Escala (1-5)',
  experience: 'Experiência profissional',
};

const toIdValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const getKnownQuestionCount = (row) => {
  const candidates = [
    row?.questions_count,
    row?.question_count,
    row?.questionsCount,
    row?.cv_questions_count,
    row?.total_questions,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === '') continue;
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }

  return null;
};

export default function Forms() {
  const location = useLocation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const [jobFilter, setJobFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [newStageDialogOpen, setNewStageDialogOpen] = useState(false);
  const [newStageTitle, setNewStageTitle] = useState('');
  const [savingNewStage, setSavingNewStage] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  const [editStageOpen, setEditStageOpen] = useState(false);
  const [editStageId, setEditStageId] = useState(null);
  const [editStageTitle, setEditStageTitle] = useState('');
  const [savingStageTitle, setSavingStageTitle] = useState(false);

  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [questionDialogFormId, setQuestionDialogFormId] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deleteQuestionTarget, setDeleteQuestionTarget] = useState(null);

  const [expandedForms, setExpandedForms] = useState({});
  const [formQuestionsMap, setFormQuestionsMap] = useState({});
  const [formQuestionsLoadingMap, setFormQuestionsLoadingMap] = useState({});
  const [formQuestionsSavingMap, setFormQuestionsSavingMap] = useState({});

  const [pendingQueryOpen, setPendingQueryOpen] = useState(null);

  const lastLoadTokenRef = useRef(0);

  const parseJobId = useCallback((value) => {
    if (value === undefined || value === null || value === 'all') return undefined;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  }, []);

  const sortForms = useCallback((list) => {
    return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
      const pa = a?.position ?? a?.order ?? a?.display_order ?? a?.sequence ?? 0;
      const pb = b?.position ?? b?.order ?? b?.display_order ?? b?.sequence ?? 0;
      if (pa !== pb) return pa - pb;
      return String(a?.created_at ?? '').localeCompare(String(b?.created_at ?? ''));
    });
  }, []);

  const sortQuestions = useCallback((list) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
      const pa = Number.isFinite(Number(a?.position)) ? Number(a.position) : Infinity;
      const pb = Number.isFinite(Number(b?.position)) ? Number(b.position) : Infinity;
      if (pa !== pb) return pa - pb;
      return String(a?.created_at ?? '').localeCompare(String(b?.created_at ?? ''));
    });
  }, []);

  const selectedJobId = useMemo(() => parseJobId(jobFilter), [jobFilter, parseJobId]);

  const load = useCallback(async (jobId) => {
    const token = ++lastLoadTokenRef.current;
    setLoading(true);
    try {
      const data = await getCvForms(jobId);
      if (token === lastLoadTokenRef.current) {
        const normalizedRows = Array.isArray(data) ? (jobId ? data : sortForms(data)) : [];
        setRows(normalizedRows);
        setExpandedForms((prev) => {
          let changed = false;
          const next = { ...prev };

          normalizedRows.forEach((row) => {
            const formId = String(row?.id ?? '');
            if (!formId) return;
            const count = getKnownQuestionCount(row);
            if (count === 0 && next[formId] === undefined) {
              next[formId] = true;
              changed = true;
            }
          });

          return changed ? next : prev;
        });
      }
    } catch (err) {
      console.error(err);
      if (token === lastLoadTokenRef.current) {
        setRows([]);
        toast.error('Erro ao carregar etapas.');
      }
    } finally {
      if (token === lastLoadTokenRef.current) setLoading(false);
    }
  }, [sortForms]);

  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const data = await getCvJobs();
      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setJobs([]);
      toast.error('Erro ao carregar vagas.');
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  const loadFormQuestions = useCallback(async (formId, options = {}) => {
    const normalizedFormId = String(formId);
    const { force = false } = options;

    if (!force && Array.isArray(formQuestionsMap[normalizedFormId])) return;

    setFormQuestionsLoadingMap((prev) => ({ ...prev, [normalizedFormId]: true }));
    try {
      const data = await getCvFormQuestions(formId);
      const ordered = sortQuestions(data);
      setFormQuestionsMap((prev) => ({ ...prev, [normalizedFormId]: ordered }));
      if (ordered.length === 0) {
        setExpandedForms((prev) => ({ ...prev, [normalizedFormId]: true }));
      }
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar perguntas da etapa.');
    } finally {
      setFormQuestionsLoadingMap((prev) => ({ ...prev, [normalizedFormId]: false }));
    }
  }, [formQuestionsMap, sortQuestions]);

  const openEditStage = useCallback(async (rowOrId) => {
    if (rowOrId && typeof rowOrId === 'object') {
      setEditStageId(toIdValue(rowOrId.id));
      setEditStageTitle(rowOrId.title || '');
      setEditStageOpen(true);
      return;
    }

    const formId = toIdValue(rowOrId);
    if (!formId) return;

    const cachedRow = rows.find((row) => String(row.id) === String(formId));
    if (cachedRow) {
      setEditStageId(formId);
      setEditStageTitle(cachedRow.title || '');
      setEditStageOpen(true);
      return;
    }

    try {
      const details = await getCvFormDetails(formId);
      setEditStageId(formId);
      setEditStageTitle(details?.title || '');
      setEditStageOpen(true);
    } catch (err) {
      console.error(err);
      toast.error('Não foi possível abrir a edição da etapa.');
    }
  }, [rows]);

  const openQuestionDialog = useCallback((formId, question = null) => {
    setQuestionDialogFormId(toIdValue(formId));
    setEditingQuestion(question || null);
    setQuestionDialogOpen(true);
  }, []);

  const handleCloseQuestionDialog = useCallback((shouldReload = false) => {
    const currentFormId = questionDialogFormId;

    setQuestionDialogOpen(false);
    setEditingQuestion(null);
    setQuestionDialogFormId(null);

    if (shouldReload && currentFormId) {
      void loadFormQuestions(currentFormId, { force: true });
      void load(selectedJobId);
    }
  }, [questionDialogFormId, loadFormQuestions, load, selectedJobId]);

  const handleSaveQuestion = useCallback(async (questionData) => {
    if (questionData.id) {
      return updateCvQuestion(questionData.id, questionData);
    }
    if (!questionDialogFormId) {
      throw new Error('ID da etapa não informado para criar pergunta.');
    }
    return createCvQuestion(questionDialogFormId, questionData);
  }, [questionDialogFormId]);

  useEffect(() => { load(selectedJobId); }, [load, selectedJobId]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jobIdParam = params.get('jobId');
    setJobFilter(jobIdParam || 'all');

    const editFormIdParam = params.get('editFormId');
    const editQuestionIdParam = params.get('editQuestionId');

    if (editFormIdParam) {
      setPendingQueryOpen({
        formId: toIdValue(editFormIdParam),
        questionId: toIdValue(editQuestionIdParam),
      });

      const sp = new URLSearchParams(location.search);
      sp.delete('editFormId');
      sp.delete('editQuestionId');
      navigate({ search: sp.toString() }, { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!pendingQueryOpen?.formId) return;

    let active = true;
    const run = async () => {
      const { formId, questionId } = pendingQueryOpen;

      if (!questionId) {
        await openEditStage(formId);
        if (active) setPendingQueryOpen(null);
        return;
      }

      try {
        const data = await getCvFormQuestions(formId);
        const ordered = sortQuestions(data);
        if (!active) return;

        setFormQuestionsMap((prev) => ({ ...prev, [String(formId)]: ordered }));
        setExpandedForms((prev) => ({ ...prev, [String(formId)]: true }));

        const targetQuestion = ordered.find((q) => String(q?.id) === String(questionId));
        openQuestionDialog(formId, targetQuestion || null);
      } catch (err) {
        console.error(err);
        toast.error('Não foi possível abrir a pergunta para edição.');
      } finally {
        if (active) setPendingQueryOpen(null);
      }
    };

    void run();

    return () => { active = false; };
  }, [pendingQueryOpen, openEditStage, openQuestionDialog, sortQuestions]);

  const onChangeJobFilter = (value) => {
    setJobFilter(value);
    const sp = new URLSearchParams(location.search);
    if (value && value !== 'all') sp.set('jobId', value);
    else sp.delete('jobId');
    navigate({ search: sp.toString() }, { replace: true });
  };

  const handleOpenNewStageDialog = () => {
    setNewStageTitle('');
    setNewStageDialogOpen(true);
  };

  const handleSaveNewStage = async () => {
    const trimmed = newStageTitle.trim();
    if (!trimmed) {
      toast.warning('Informe um título para a etapa.');
      return;
    }

    setSavingNewStage(true);
    try {
      const payload = { title: trimmed };
      if (selectedJobId) payload.job_id = selectedJobId;

      const createdStage = await createCvForm(payload);
      if (createdStage?.id) {
        const normalizedCreatedStage = {
          ...createdStage,
          title: createdStage.title || trimmed,
          job_id: createdStage.job_id ?? selectedJobId ?? null,
          questions_count: 0,
        };
        setRows((prev) => {
          if (prev.some((row) => String(row?.id) === String(createdStage.id))) return prev;
          const next = [...prev, normalizedCreatedStage];
          return selectedJobId ? next : sortForms(next);
        });
        setExpandedForms((prev) => ({ ...prev, [String(createdStage.id)]: true }));
        setFormQuestionsMap((prev) => ({ ...prev, [String(createdStage.id)]: [] }));
      }
      toast.success('Etapa criada com sucesso.');
      setNewStageDialogOpen(false);
      setNewStageTitle('');
      await load(selectedJobId);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar etapa.');
    } finally {
      setSavingNewStage(false);
    }
  };

  const handleSaveStageTitle = async () => {
    const trimmed = editStageTitle.trim();
    if (!trimmed) {
      toast.warning('Informe um título para a etapa.');
      return;
    }
    if (!editStageId) return;

    setSavingStageTitle(true);
    try {
      await updateCvForm(editStageId, { title: trimmed });
      setRows((prev) => prev.map((row) => (
        String(row.id) === String(editStageId)
          ? { ...row, title: trimmed }
          : row
      )));
      toast.success('Título da etapa atualizado.');
      setEditStageOpen(false);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao atualizar título da etapa.');
    } finally {
      setSavingStageTitle(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCvForm(deleteId);
      toast.success('Etapa removida.');
      await load(selectedJobId);

      setExpandedForms((prev) => {
        const next = { ...prev };
        delete next[String(deleteId)];
        return next;
      });
      setFormQuestionsMap((prev) => {
        const next = { ...prev };
        delete next[String(deleteId)];
        return next;
      });
    } catch (err) {
      console.error(err);
      toast.error('Erro ao remover etapa.');
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteQuestion = async () => {
    const questionId = deleteQuestionTarget?.questionId;
    const formId = deleteQuestionTarget?.formId;
    if (!questionId || !formId) {
      setDeleteQuestionTarget(null);
      return;
    }

    try {
      await deleteCvQuestion(questionId);
      toast.success('Pergunta removida.');
      await loadFormQuestions(formId, { force: true });
    } catch (err) {
      console.error(err);
      toast.error('Erro ao remover pergunta.');
    } finally {
      setDeleteQuestionTarget(null);
    }
  };

  const handleToggleQuestions = (formId) => {
    const normalizedFormId = String(formId);
    const willOpen = !expandedForms[normalizedFormId];
    setExpandedForms((prev) => ({ ...prev, [normalizedFormId]: willOpen }));
    if (willOpen) void loadFormQuestions(formId);
  };

  const handleReorderQuestion = async (formId, index, direction) => {
    const normalizedFormId = String(formId);
    const currentList = Array.isArray(formQuestionsMap[normalizedFormId])
      ? formQuestionsMap[normalizedFormId]
      : [];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= currentList.length) return;

    const reordered = [...currentList];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];

    const ids = reordered.map((question) => question?.id).filter(Boolean);
    if (ids.length !== reordered.length) return;

    setFormQuestionsMap((prev) => ({ ...prev, [normalizedFormId]: reordered }));
    setFormQuestionsSavingMap((prev) => ({ ...prev, [normalizedFormId]: true }));

    try {
      await reorderCvQuestions(formId, ids);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao reordenar perguntas.');
      setFormQuestionsMap((prev) => ({ ...prev, [normalizedFormId]: currentList }));
      await loadFormQuestions(formId, { force: true });
    } finally {
      setFormQuestionsSavingMap((prev) => ({ ...prev, [normalizedFormId]: false }));
    }
  };

  const jobsById = useMemo(() => {
    const map = {};
    jobs.forEach((job) => {
      map[String(job.id)] = job.title || `Vaga ${job.id}`;
    });
    return map;
  }, [jobs]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) => {
      const jobName = row?.job_title || row?.job_name || jobsById[String(row?.job_id)] || '';
      return (
        String(row?.title || '').toLowerCase().includes(query)
        || String(jobName).toLowerCase().includes(query)
      );
    });
  }, [rows, search, jobsById]);

  const renderQuestionsPanel = (formId) => {
    const normalizedFormId = String(formId);
    const questions = Array.isArray(formQuestionsMap[normalizedFormId])
      ? formQuestionsMap[normalizedFormId]
      : [];
    const loadingQuestions = Boolean(formQuestionsLoadingMap[normalizedFormId]);
    const savingQuestions = Boolean(formQuestionsSavingMap[normalizedFormId]);

    return (
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: 'grey.50',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Perguntas da etapa
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {savingQuestions && (
              <Typography variant="caption" color="text.secondary">
                Salvando ordem...
              </Typography>
            )}
            <Button
              size="small"
              startIcon={<Add fontSize="small" />}
              onClick={() => openQuestionDialog(formId)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Adicionar pergunta
            </Button>
          </Stack>
        </Stack>

        {loadingQuestions && (
          <Typography variant="body2" color="text.secondary">
            Carregando perguntas...
          </Typography>
        )}

        {!loadingQuestions && questions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nenhuma pergunta cadastrada nesta etapa.
          </Typography>
        )}

        {!loadingQuestions && questions.length > 0 && (
          <Stack spacing={1}>
            {questions.map((question, index) => {
              const typeLabel = QUESTION_TYPE_LABELS[String(question?.type || '').toLowerCase()]
                || question?.type
                || 'Pergunta';

              return (
                <Stack
                  key={`question-row-${formId}-${question?.id ?? index}`}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    py: 0.75,
                    px: 1,
                    borderRadius: 1,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {index + 1}. {question?.label || '(Pergunta sem texto)'}
                    </Typography>
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Chip size="small" variant="outlined" label={typeLabel} />
                      {question?.required && (
                        <Chip size="small" color="primary" variant="outlined" label="Obrigatória" />
                      )}
                      {Array.isArray(question?.rules) && question.rules.length > 0 && (
                        <Chip
                          size="small"
                          color="error"
                          variant="outlined"
                          label="Regra de reprovação ativa"
                        />
                      )}
                    </Stack>
                  </Box>

                  <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                    <Tooltip title="Mover para cima">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleReorderQuestion(formId, index, -1)}
                          disabled={savingQuestions || index === 0}
                          aria-label="Mover para cima"
                        >
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Mover para baixo">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleReorderQuestion(formId, index, 1)}
                          disabled={savingQuestions || index === questions.length - 1}
                          aria-label="Mover para baixo"
                        >
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Editar pergunta">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openQuestionDialog(formId, question)}
                          disabled={!question?.id}
                          aria-label="Editar pergunta"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Excluir pergunta">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteQuestionTarget({
                            formId,
                            questionId: question?.id,
                          })}
                          disabled={!question?.id}
                          aria-label="Excluir pergunta"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2,
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          background: 'linear-gradient(135deg, #f8fbff 0%, #f2f7ff 100%)',
        }}
      >
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Gerenciamento de Etapas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Organize etapas e perguntas com uma visão clara do funil de candidatura.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${rows.length} ${rows.length === 1 ? 'etapa cadastrada' : 'etapas cadastradas'}`}
                />
              </Stack>
            </Box>

            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={handleOpenNewStageDialog}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 170 },
              }}
            >
              Nova etapa
            </Button>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              select
              size="small"
              label="Filtrar por vaga"
              value={jobFilter}
              onChange={(e) => onChangeJobFilter(e.target.value)}
              sx={{ width: { xs: '100%', md: 280 } }}
              disabled={loadingJobs && jobs.length === 0}
            >
              <MenuItem value="all">Todas as vagas</MenuItem>
              {jobs.map((job) => (
                <MenuItem key={job.id} value={job.id}>
                  {job.title || `Vaga ${job.id}`}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              fullWidth
              placeholder="Buscar etapa por título ou vaga"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {loading && <LinearProgress />}

        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table
              size="small"
              sx={{
                '& th:first-of-type, & td:first-of-type': {
                  pl: 3,
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ minWidth: 260, fontWeight: 700 }}>Título</TableCell>
                  <TableCell align="right" sx={{ width: 160, fontWeight: 700 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row) => {
                  const normalizedFormId = String(row.id);
                  const isExpanded = Boolean(expandedForms[normalizedFormId]);
                  const jobName = row?.job_title || row?.job_name || jobsById[String(row?.job_id)] || '';

                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        hover
                        onClick={() => handleToggleQuestions(row.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Stack spacing={0.2}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.title || '(Etapa sem título)'}
                            </Typography>
                            {jobName && (
                              <Typography variant="caption" color="text.secondary">
                                Vaga: {jobName}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={isExpanded ? 'Ocultar perguntas' : 'Ver perguntas'}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleQuestions(row.id);
                              }}
                              aria-label={isExpanded ? 'Ocultar perguntas' : 'Ver perguntas'}
                            >
                              {isExpanded ? <QuestionAnswer fontSize="small" /> : <QuestionAnswerOutlined fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar etapa">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                void openEditStage(row);
                              }}
                              aria-label="Editar etapa"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir etapa">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(row.id);
                              }}
                              aria-label="Excluir etapa"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ py: 1.5 }}>
                            {renderQuestionsPanel(row.id)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}

                {!loading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography sx={{ py: 3 }} color="text.secondary">
                        {search ? 'Nenhuma etapa encontrada para sua busca.' : 'Nenhuma etapa cadastrada.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.25 }}>
          <Stack spacing={1.25}>
            {filtered.map((row) => {
              const normalizedFormId = String(row.id);
              const isExpanded = Boolean(expandedForms[normalizedFormId]);
              const jobName = row?.job_title || row?.job_name || jobsById[String(row?.job_id)] || '';

              return (
                <Paper
                  key={`mobile-${row.id}`}
                  variant="outlined"
                  onClick={() => handleToggleQuestions(row.id)}
                  sx={{ p: 1.5, borderRadius: 1.5, cursor: 'pointer' }}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.title || '(Etapa sem título)'}
                        </Typography>
                        {jobName && (
                          <Typography variant="caption" color="text.secondary">
                            Vaga: {jobName}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={isExpanded ? 'Ocultar perguntas' : 'Ver perguntas'}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleQuestions(row.id);
                          }}
                          aria-label={isExpanded ? 'Ocultar perguntas' : 'Ver perguntas'}
                        >
                          {isExpanded ? <QuestionAnswer fontSize="small" /> : <QuestionAnswerOutlined fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar etapa">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            void openEditStage(row);
                          }}
                          aria-label="Editar etapa"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir etapa">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(row.id);
                          }}
                          aria-label="Excluir etapa"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {isExpanded && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        {renderQuestionsPanel(row.id)}
                      </Box>
                    )}
                  </Stack>
                </Paper>
              );
            })}

            {!loading && filtered.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography align="center" color="text.secondary">
                  {search ? 'Nenhuma etapa encontrada para sua busca.' : 'Nenhuma etapa cadastrada.'}
                </Typography>
              </Paper>
            )}
          </Stack>
        </Box>
      </Paper>

      <Dialog open={newStageDialogOpen} onClose={() => !savingNewStage && setNewStageDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nova etapa</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Título"
            value={newStageTitle}
            onChange={(e) => setNewStageTitle(e.target.value.slice(0, 200))}
            required
            autoFocus
            disabled={savingNewStage}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 0.75 }}
          />
          <Typography variant="caption" color="text.secondary">
            Dica: com uma vaga filtrada, a etapa será vinculada automaticamente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewStageDialogOpen(false)} disabled={savingNewStage}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveNewStage} disabled={savingNewStage}>
            {savingNewStage ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editStageOpen} onClose={() => setEditStageOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar etapa</DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 0.5 }}>
          <TextField
            label="Título da etapa"
            value={editStageTitle}
            onChange={(e) => setEditStageTitle(e.target.value.slice(0, 200))}
            fullWidth
            autoFocus
            required
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 0.75 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditStageOpen(false)} disabled={savingStageTitle}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveStageTitle} disabled={savingStageTitle}>
            {savingStageTitle ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {questionDialogOpen && (
        <QuestionDialog
          open={questionDialogOpen}
          onClose={handleCloseQuestionDialog}
          onSave={handleSaveQuestion}
          question={editingQuestion}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteId)}
        title="Excluir etapa"
        message="Deseja realmente excluir esta etapa e todas as perguntas vinculadas?"
        onConfirm={handleDelete}
        onClose={() => setDeleteId(null)}
      />

      <ConfirmDialog
        open={Boolean(deleteQuestionTarget?.questionId)}
        title="Excluir pergunta"
        message="Deseja realmente excluir esta pergunta?"
        onConfirm={handleDeleteQuestion}
        onClose={() => setDeleteQuestionTarget(null)}
      />
    </Box>
  );
}
